import DataTable from "../../components/common/DataTable";
import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { notifications } from "@mantine/notifications";
import { Text } from "@mantine/core";
import {
  AnalyticsPageLayout,
  StatCard,
  ChartCard,
  TOOLTIP_STYLE,
  chartProps,
} from "./AnalyticsLayout";
import { Megaphone } from "lucide-react";

const adCampaigns = [
  { key: "1", campaign: "Summer Action Blockbuster", type: "Video", impressions: 2850000, clicks: 142500, ctr: 5.0, revenue: 28500, cpm: 10.0 },
  { key: "2", campaign: "Premium Subscription Promo", type: "Display", impressions: 1950000, clicks: 78000, ctr: 4.0, revenue: 19500, cpm: 10.0 },
  { key: "3", campaign: "New Release Announcement", type: "Video", impressions: 3200000, clicks: 160000, ctr: 5.0, revenue: 32000, cpm: 10.0 },
  { key: "4", campaign: "Festival Season Special", type: "Native", impressions: 1580000, clicks: 63200, ctr: 4.0, revenue: 15800, cpm: 10.0 },
];

const adTypes = [
  { name: "Pre-roll", value: 35, color: "#3b82f6" },
  { name: "Mid-roll", value: 25, color: "#10b981" },
  { name: "Post-roll", value: 20, color: "#f59e0b" },
  { name: "Banner", value: 15, color: "#8b5cf6" },
  { name: "Native", value: 5, color: "#06b6d4" },
];

const adColumns = [
  { title: "Campaign Name", dataIndex: "campaign", key: "campaign" },
  { title: "Type", dataIndex: "type", key: "type" },
  { title: "Impressions", dataIndex: "impressions", key: "impressions", render: (t) => t.toLocaleString() },
  { title: "Clicks", dataIndex: "clicks", key: "clicks", render: (t) => t.toLocaleString() },
  { title: "CTR", dataIndex: "ctr", key: "ctr", render: (t) => `${t.toFixed(1)}%` },
  { title: "Revenue", dataIndex: "revenue", key: "revenue", render: (t) => `Tsh ${t.toLocaleString()}` },
  { title: "CPM", dataIndex: "cpm", key: "cpm", render: (t) => `Tsh ${t.toFixed(2)}` },
];

const adRevenueTrend = [
  { week: "Week 1", revenue: 45000, impressions: 8500000, clicks: 425000 },
  { week: "Week 2", revenue: 52000, impressions: 9200000, clicks: 460000 },
  { week: "Week 3", revenue: 58000, impressions: 10100000, clicks: 505000 },
  { week: "Week 4", revenue: 65000, impressions: 11200000, clicks: 560000 },
];

const adPlacementPerformance = [
  { placement: "Pre-roll", ctr: 6.5, cpm: 12.5, rpm: 8.1 },
  { placement: "Mid-roll", ctr: 4.2, cpm: 8.75, rpm: 3.7 },
  { placement: "Post-roll", ctr: 3.1, cpm: 6.25, rpm: 1.9 },
  { placement: "Banner", ctr: 2.8, cpm: 5.0, rpm: 1.4 },
  { placement: "Native", ctr: 8.2, cpm: 15.0, rpm: 12.3 },
];

const AdAnalyticsPage = () => {
  const handleExport = (format) => {
    notifications.show({ message: `Exporting as ${format.toUpperCase()}...`, color: "green" });
  };

  return (
    <AnalyticsPageLayout
      icon={Megaphone}
      title="Ad Analytics"
      subtitle="Advertising campaigns & revenue metrics"
      onExport={handleExport}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Impressions" value={39580000} suffix="" />
        <StatCard title="Total Clicks" value={1603700} suffix="" />
        <StatCard title="Ad Revenue" prefix="Tsh " value={220000} />
        <StatCard title="Avg CTR" value={4.06} suffix="%" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="Ad Revenue Trend">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={adRevenueTrend}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="week" {...chartProps.axis} />
                <YAxis {...chartProps.axis} tickFormatter={(v) => v.toLocaleString()} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2} name="Revenue" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
        <ChartCard title="Ad Type Distribution">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={adTypes}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name} ${value}%`}
                  labelLine={false}
                >
                  {adTypes.map((entry, i) => (
                    <Cell key={i} fill={entry.color} stroke="#0a0a0a" strokeWidth={2} />
                  ))}
                </Pie>
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n, p) => [`${v}%`, p.payload.name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="mb-8">
      <ChartCard title="Ad Placement Performance">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {adPlacementPerformance.map((p, i) => (
            <div key={i} className="exec-stat-card p-4 rounded-lg text-center">
              <Text size="xs" c="dimmed" mb={8} fw={600}>{p.placement}</Text>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div><Text size="xs" c="dimmed">CTR</Text><Text size="sm" fw={600} c="blue.4">{p.ctr}%</Text></div>
                <div><Text size="xs" c="dimmed">CPM</Text><Text size="sm" fw={600} c="green.4">Tsh {p.cpm}</Text></div>
                <div><Text size="xs" c="dimmed">RPM</Text><Text size="sm" fw={600} c="yellow.4">Tsh {p.rpm}</Text></div>
              </div>
            </div>
          ))}
        </div>
      </ChartCard>
      </div>

      <ChartCard title="Ad Campaigns Performance">
        <DataTable dataSource={adCampaigns} columns={adColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
      </ChartCard>
    </AnalyticsPageLayout>
  );
};

export default AdAnalyticsPage;
